/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Copy, RefreshCw, XCircle, Search, MailPlus, Download, CheckCircle2, Users, Sparkles } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import EditableEmailPreview from "@/components/EditableEmailPreview";
import { relativeTime, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const INVITE_STATUS = {
    pending: "Pending",
    used: "Activated",
    revoked: "Revoked",
    expired: "Expired",
};

const STATUS_TONE = {
    pending: "blue",
    used: "green",
    revoked: "grey",
    expired: "amber",
};

function buildActivationUrl(token) {
    return `${window.location.origin}/activate/${token}`;
}

export default function FirmInvites() {
    const { user } = useAuth();
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [groups, setGroups] = useState([]);
    const [groupsBusy, setGroupsBusy] = useState({});
    // Group-invite preview flow (mirrors resend flow: preview → edit → send)
    const [groupTarget, setGroupTarget] = useState(null);   // the group being invited
    const [groupPreview, setGroupPreview] = useState(null);
    const [groupBusy, setGroupBusy] = useState(false);
    const groupEditorRef = useRef(null);
    // Custom group invite builder (for entities with DIFFERENT contact_emails)
    const [customOpen, setCustomOpen] = useState(false);
    const [allClients, setAllClients] = useState([]);
    const [portalUsers, setPortalUsers] = useState([]);
    const [customEmail, setCustomEmail] = useState("");
    const [customName, setCustomName] = useState("");
    const [customPickedIds, setCustomPickedIds] = useState([]);
    const [customSearch, setCustomSearch] = useState("");
    const [customLoading, setCustomLoading] = useState(false);
    // Resend preview flow
    const [resendTarget, setResendTarget] = useState(null);   // the invite being resent
    const [resendPreview, setResendPreview] = useState(null);
    const [resendBusy, setResendBusy] = useState(false);
    const resendEditorRef = useRef(null);

    async function loadGroups() {
        try {
            const { data } = await api.get("/invites/grouped-owners");
            setGroups(data);
        } catch (e) {
            // Non-critical — hide section if it fails
            setGroups([]);
        }
    }
    useEffect(() => { loadGroups(); }, []);

    async function load() {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter;
            const { data } = await api.get("/invites/", { params });
            setInvites(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); /* eslint-disable-line */ }, [statusFilter]);

    const filtered = useMemo(() => {
        if (!search) return invites;
        const q = search.toLowerCase();
        return invites.filter((i) =>
            (i.client_name || "").toLowerCase().includes(q) ||
            (i.email || "").toLowerCase().includes(q) ||
            (i.name || "").toLowerCase().includes(q)
        );
    }, [invites, search]);

    async function copyLink(token) {
        const url = buildActivationUrl(token);
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Activation link copied");
        } catch (e) {
            window.prompt("Copy this activation link:", url);
        }
    }

    async function revoke(invite) {
        if (!confirm(`Revoke invite for ${invite.client_name}?`)) return;
        try {
            await api.post(`/invites/${invite.id}/revoke`);
            toast.success("Invite revoked");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function openResendPreview(invite) {
        setResendTarget(invite);
        setResendPreview(null);
        setResendBusy(true);
        try {
            const payload = invite.client_ids?.length
                ? { client_ids: invite.client_ids, name: invite.name, email: invite.email }
                : { client_id: invite.client_id, name: invite.name, email: invite.email };
            const { data } = await api.post("/invites/preview", payload);
            setResendPreview(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setResendTarget(null);
        } finally {
            setResendBusy(false);
        }
    }

    async function confirmResend() {
        if (!resendTarget) return;
        setResendBusy(true);
        try {
            const edited = resendEditorRef.current?.getValues();
            const body = {};
            if (edited?.subject && edited.subject !== resendPreview?.subject) body.subject = edited.subject;
            if (edited?.html && edited.html !== resendPreview?.html) body.html = edited.html;
            const { data } = await api.post(`/invites/${resendTarget.id}/resend`, body);
            await load();
            await copyLink(data.invite.token);
            const editedNote = (body.subject || body.html) ? " (with your edits)" : "";
            toast.success(`Invite email resent${editedNote} — fresh link copied to your clipboard.`);
            setResendTarget(null);
            setResendPreview(null);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setResendBusy(false);
        }
    }

    async function runBulk() {
        setBulkBusy(true);
        setBulkResult(null);
        try {
            const { data } = await api.post("/invites/bulk");
            setBulkResult(data);
            await load();
            const created = data.created || 0;
            if (created > 0) toast.success(`${created} new invites generated`);
            else toast.info("No new invites — everyone with an email already has one");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBulkBusy(false);
        }
    }

    async function inviteGroup(group) {
        // Fast-path when the portal user already exists — no email will go
        // out, we're just linking extra client_ids to their login.
        if (group.existing_user) {
            setGroupsBusy((m) => ({ ...m, [group.email]: true }));
            try {
                const { data } = await api.post("/invites/group", {
                    email: group.email,
                    client_ids: group.clients.map((c) => c.id),
                    name: group.clients[0]?.name,
                });
                if (data.status === "appended_to_existing_user") {
                    toast.success(`Linked ${group.clients.length} entities to existing portal user`);
                } else {
                    toast.success("Group invite updated");
                }
                await loadGroups();
                await load();
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setGroupsBusy((m) => ({ ...m, [group.email]: false }));
            }
            return;
        }
        // Fresh invite path — always open the editable preview first so the
        // firm can eyeball the email and confirm the full entity list before
        // it goes out.
        setGroupTarget(group);
        setGroupPreview(null);
        setGroupBusy(true);
        try {
            const { data } = await api.post("/invites/preview", {
                client_ids: group.clients.map((c) => c.id),
                email: group.email,
                name: group.clients[0]?.name,
            });
            setGroupPreview(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setGroupTarget(null);
        } finally {
            setGroupBusy(false);
        }
    }

    async function confirmGroupInvite() {
        if (!groupTarget) return;
        setGroupBusy(true);
        try {
            const edited = groupEditorRef.current?.getValues();
            const body = {
                email: groupTarget.email,
                client_ids: groupTarget.clients.map((c) => c.id),
                name: groupTarget.clients[0]?.name,
            };
            if (edited?.subject && edited.subject !== groupPreview?.subject) body.subject = edited.subject;
            if (edited?.html && edited.html !== groupPreview?.html) body.html = edited.html;
            const { data } = await api.post("/invites/group", body);
            if (data?.invite?.token) {
                const url = `${window.location.origin}/activate/${data.invite.token}`;
                try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
                const editedNote = (body.subject || body.html) ? " (with your edits)" : "";
                toast.success(`Group invite sent${editedNote} — link copied (${groupTarget.clients.length} entities)`);
            } else {
                toast.success("Group invite updated");
            }
            setGroupTarget(null);
            setGroupPreview(null);
            await loadGroups();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setGroupBusy(false);
        }
    }

    async function openCustomBuilder() {
        setCustomOpen(true);
        setCustomEmail("");
        setCustomName("");
        setCustomPickedIds([]);
        setCustomSearch("");
        setCustomLoading(true);
        try {
            const [clientsResp, usersResp] = await Promise.all([
                api.get("/clients/"),
                api.get("/auth/users"),
            ]);
            setAllClients((clientsResp.data || []).filter((c) => c.active !== false));
            setPortalUsers((usersResp.data || []).filter((u) => u.role === "client"));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCustomLoading(false);
        }
    }

    async function launchCustomPreview() {
        const email = (customEmail || "").trim().toLowerCase();
        if (!email || !customPickedIds.length) return;
        const picked = allClients.filter((c) => customPickedIds.includes(c.id));
        const existingUser = portalUsers.find((u) => (u.email || "").toLowerCase() === email);
        const synthetic = {
            email,
            count: picked.length,
            clients: picked.map((c) => ({ id: c.id, name: c.name })),
            existing_user: existingUser || null,
        };
        setCustomOpen(false);
        if (existingUser) {
            // Fast-path: portal user already exists — just link, no email sent.
            setGroupsBusy((m) => ({ ...m, [email]: true }));
            try {
                const { data } = await api.post("/invites/group", {
                    email,
                    client_ids: customPickedIds,
                    name: customName || picked[0]?.name,
                });
                if (data.status === "appended_to_existing_user") {
                    toast.success(`Linked ${picked.length} ${picked.length === 1 ? "entity" : "entities"} to ${email}`);
                } else {
                    toast.success("Group invite updated");
                }
                await loadGroups();
                await load();
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setGroupsBusy((m) => ({ ...m, [email]: false }));
            }
            return;
        }
        // Fresh invite → editable preview.
        setGroupTarget(synthetic);
        setGroupPreview(null);
        setGroupBusy(true);
        try {
            const { data } = await api.post("/invites/preview", {
                client_ids: customPickedIds,
                email,
                name: customName || picked[0]?.name,
            });
            setGroupPreview(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setGroupTarget(null);
        } finally {
            setGroupBusy(false);
        }
    }


    function exportCsv() {
        const rows = [["Client", "Name", "Email", "Status", "Activation URL", "Created"]];
        filtered.forEach((i) => {
            rows.push([
                i.client_name || "",
                i.name || "",
                i.email || "",
                i.status,
                i.status === "pending" && i.activation_url ? i.activation_url : "",
                i.created_at || "",
            ]);
        });
        const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `clientops-invites-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
    }

    const isAdmin = user?.role === "firm_admin";
    const counts = useMemo(() => {
        const c = { pending: 0, used: 0, revoked: 0, expired: 0 };
        invites.forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
        return c;
    }, [invites]);

    return (
        <div>
            <PageHeader
                eyebrow="Portal invites"
                title="Invite clients to their portal"
                description="Magic-link invites — generate, copy, and share. No email setup needed; activation links work for 30 days and convert into a client account when clicked."
                actions={
                    <div className="flex gap-2 flex-wrap">
                        <Button variant="outline" onClick={exportCsv} disabled={filtered.length === 0} data-testid="invites-export-csv">
                            <Download className="h-4 w-4" /> Export CSV
                        </Button>
                        <Button
                            variant="outline"
                            onClick={openCustomBuilder}
                            disabled={!isAdmin}
                            data-testid="invites-custom-group-btn"
                        >
                            <Users className="h-4 w-4" /> Group invite (different emails)
                        </Button>
                        <Button
                            onClick={() => setBulkOpen(true)}
                            disabled={!isAdmin}
                            className="bg-[#171717] hover:bg-[#2E2E2E] text-white"
                            data-testid="invites-bulk-btn"
                        >
                            <MailPlus className="h-4 w-4" /> Bulk invite
                        </Button>
                    </div>
                }
                testId="invites-header"
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Tile n={counts.pending || 0} l="Pending" tone="blue" testId="tile-pending" />
                <Tile n={counts.used || 0} l="Activated" tone="green" testId="tile-used" />
                <Tile n={counts.revoked || 0} l="Revoked" tone="grey" testId="tile-revoked" />
                <Tile n={counts.expired || 0} l="Expired" tone="amber" testId="tile-expired" />
            </div>

            {/* Multi-entity owners — emails that own >1 clients */}
            {groups.length > 0 && (
                <div className="mt-6 co-card co-card-pad" data-testid="grouped-owners-section">
                    <div className="flex items-center gap-2">
                        <div className="h-8 w-8 grid place-items-center rounded-full bg-emerald-50 text-emerald-700">
                            <Users className="h-4 w-4" />
                        </div>
                        <div>
                            <div className="co-label">Multi-entity portal owners</div>
                            <div className="text-sm font-medium mt-0.5">
                                {groups.length} email{groups.length === 1 ? "" : "s"} own more than one entity
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-[#5E636E] mt-2">
                        These contacts manage <strong>multiple businesses</strong> from one inbox (eg. a company + their personal IRD + a family trust). Inviting them as one portal owner means they sign in once and switch between entities — instead of juggling separate logins.
                    </p>
                    <div className="mt-4 grid gap-2.5">
                        {groups.slice(0, 12).map((g) => (
                            <div
                                key={g.email}
                                className="rounded-md border border-[#E4E4E7] bg-white p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                                data-testid={`grouped-owner-${g.email}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium font-mono truncate" data-testid={`grouped-owner-email-${g.email}`}>{g.email}</div>
                                    <div className="text-[11px] text-[#5E636E] mt-1 leading-relaxed">
                                        <span className="font-medium text-[#0F1115]">{g.count} entities</span>
                                    </div>
                                    <div
                                        className="mt-1 flex flex-wrap gap-1"
                                        data-testid={`grouped-owner-entities-${g.email}`}
                                    >
                                        {g.clients.map((c) => (
                                            <span
                                                key={c.id}
                                                className="inline-flex items-center rounded-md border border-[#E4E4E7] bg-[#F8F8F5] px-2 py-0.5 text-[11px] text-[#333] max-w-[280px] truncate"
                                                title={c.name}
                                                data-testid={`grouped-owner-entity-${g.email}-${c.id}`}
                                            >
                                                {c.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                {g.all_linked ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] text-emerald-800" data-testid={`grouped-owner-status-${g.email}`}>
                                        <CheckCircle2 className="h-3 w-3" /> All linked
                                    </span>
                                ) : g.existing_user ? (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!isAdmin || groupsBusy[g.email]}
                                        onClick={() => inviteGroup(g)}
                                        data-testid={`grouped-owner-merge-${g.email}`}
                                    >
                                        {groupsBusy[g.email] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                        Add missing to existing login
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        className="bg-[#171717] hover:bg-[#2E2E2E] text-white"
                                        disabled={!isAdmin || groupsBusy[g.email]}
                                        onClick={() => inviteGroup(g)}
                                        data-testid={`grouped-owner-invite-${g.email}`}
                                    >
                                        {groupsBusy[g.email] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                        Invite as one portal owner
                                    </Button>
                                )}
                            </div>
                        ))}
                        {groups.length > 12 && (
                            <div className="text-[11px] text-[#5E636E] pt-1">
                                And {groups.length - 12} more groups. Process top ones first; refresh after activation.
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="mt-6 co-card co-card-pad flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                    <Label className="text-[11px] text-[#5E636E]">Search</Label>
                    <div className="relative mt-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5E636E]" />
                        <Input
                            className="pl-9"
                            placeholder="Client, name or email…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="invites-search"
                        />
                    </div>
                </div>
                <div>
                    <Label className="text-[11px] text-[#5E636E]">Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="mt-1 min-w-[180px]" data-testid="invites-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="used">Activated</SelectItem>
                            <SelectItem value="revoked">Revoked</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="mt-6">
                {loading ? (
                    <div className="text-sm text-[#5E636E]">Loading invites…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={Send}
                        title="No invites yet"
                        description="Hit Bulk invite to generate one for every client that has an email and no portal user."
                        testId="invites-empty"
                    />
                ) : (
                    <div className="co-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="invites-table">
                            <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#5E636E]">
                                <tr>
                                    <th className="text-left px-4 py-2">Client</th>
                                    <th className="text-left px-4 py-2">Email</th>
                                    <th className="text-left px-4 py-2">Status</th>
                                    <th className="text-left px-4 py-2">Created</th>
                                    <th className="text-left px-4 py-2">Expires</th>
                                    <th className="text-right px-4 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E4E4E7]">
                                {filtered.map((inv) => (
                                    <tr key={inv.id} data-testid={`invite-row-${inv.id}`}>
                                        <td className="px-4 py-2 max-w-[260px]">
                                            <div className="truncate font-medium">{inv.client_name}</div>
                                            <div className="text-[11px] text-[#5E636E] truncate">{inv.name}</div>
                                        </td>
                                        <td className="px-4 py-2 font-mono text-xs">{inv.email}</td>
                                        <td className="px-4 py-2"><StatusBadge label={INVITE_STATUS[inv.status] || inv.status} tone={STATUS_TONE[inv.status]} /></td>
                                        <td className="px-4 py-2 text-xs text-[#5E636E]">{relativeTime(inv.created_at)}</td>
                                        <td className="px-4 py-2 text-xs text-[#5E636E]">{formatDate(inv.expires_at)}</td>
                                        <td className="px-4 py-2">
                                            <div className="flex justify-end gap-1.5">
                                                {inv.status === "pending" && (
                                                    <>
                                                        <Button size="sm" variant="outline" onClick={() => copyLink(inv.token)} data-testid={`invite-copy-${inv.id}`}>
                                                            <Copy className="h-3.5 w-3.5" /> Copy link
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={() => openResendPreview(inv)} data-testid={`invite-resend-${inv.id}`} title="Preview the same activation email before resending">
                                                            <RefreshCw className="h-3.5 w-3.5" /> Resend email
                                                        </Button>
                                                        {isAdmin && (
                                                            <Button size="sm" variant="outline" onClick={() => revoke(inv)} data-testid={`invite-revoke-${inv.id}`}>
                                                                <XCircle className="h-3.5 w-3.5" />
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                                {inv.status === "used" && (
                                                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Activated {relativeTime(inv.used_at)}</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Bulk invite dialog */}
            <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkResult(null); } }}>
                <DialogContent className="max-w-lg" data-testid="bulk-invite-dialog">
                    <DialogHeader><DialogTitle>Bulk invite clients</DialogTitle></DialogHeader>
                    {!bulkResult ? (
                        <>
                            <p className="text-sm text-[#5E636E]">
                                We&apos;ll generate a magic-link invite for every active client in your firm that:
                            </p>
                            <ul className="mt-3 space-y-1.5 text-sm text-[#0F1115]">
                                <li>· has a valid email on file</li>
                                <li>· doesn&apos;t already have a portal user</li>
                                <li>· doesn&apos;t already have a pending invite</li>
                            </ul>
                            <p className="text-xs text-[#5E636E] mt-4">
                                Once generated, copy each link from the table and share it however you like (email, SMS, secure message). Links work for 30 days and become a real account on first use.
                            </p>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel">Cancel</Button>
                                <Button onClick={runBulk} disabled={bulkBusy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="bulk-confirm">
                                    {bulkBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : "Generate invites"}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-3" data-testid="bulk-result-summary">
                                <ResultStat n={bulkResult.created} l="New invites created" tone="green" />
                                <ResultStat n={bulkResult.skipped_already_invited} l="Already had a pending invite" />
                                <ResultStat n={bulkResult.skipped_no_email} l="Skipped — no email" tone="amber" />
                                <ResultStat n={bulkResult.skipped_already_has_user} l="Skipped — already activated" />
                            </div>
                            <p className="text-xs text-[#5E636E] mt-2">
                                Hit Export CSV on the main page to download a sheet of all activation links to mail-merge from your own email tool.
                            </p>
                            <DialogFooter>
                                <Button className="bg-[#171717] hover:bg-[#2E2E2E] text-white" onClick={() => { setBulkOpen(false); setBulkResult(null); }} data-testid="bulk-close">Done</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Resend preview dialog */}
            <Dialog open={!!resendTarget} onOpenChange={(o) => { if (!o) { setResendTarget(null); setResendPreview(null); } }}>
                <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="invite-resend-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit &amp; resend invite email</DialogTitle>
                        <DialogDescription>
                            {resendTarget
                                ? `Edit any wording inline before sending to ${resendTarget.email || "the client"}. A fresh 30-day activation link is minted when you hit Resend.`
                                : ""}
                        </DialogDescription>
                    </DialogHeader>
                    {resendBusy && !resendPreview ? (
                        <div className="flex items-center gap-2 text-sm text-[#5E636E] py-8 justify-center">
                            <Loader2 className="h-4 w-4 animate-spin" /> Rendering preview…
                        </div>
                    ) : resendPreview ? (
                        <EditableEmailPreview
                            ref={resendEditorRef}
                            initialSubject={resendPreview.subject}
                            initialHtml={resendPreview.html}
                            toEmail={resendPreview.to || resendTarget?.email}
                        />
                    ) : null}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setResendTarget(null); setResendPreview(null); }} disabled={resendBusy} data-testid="invite-resend-cancel">Cancel</Button>
                        <Button
                            onClick={confirmResend}
                            disabled={resendBusy || !resendPreview}
                            className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                            data-testid="invite-resend-confirm"
                        >
                            {resendBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4" /> Resend email</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Group-invite editable preview dialog */}
            <Dialog
                open={!!groupTarget}
                onOpenChange={(o) => { if (!o) { setGroupTarget(null); setGroupPreview(null); } }}
            >
                <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="group-invite-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit &amp; send group invite</DialogTitle>
                        <DialogDescription>
                            {groupTarget
                                ? `One email covers ${groupTarget.clients.length} entities and goes to ${groupTarget.email}. Tweak wording before sending — the activation link is minted when you hit Send.`
                                : ""}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Full entity list — always visible so firm confirms the scope */}
                    {groupTarget && (
                        <div className="rounded-md border border-[#E4E4E7] bg-[#F8F8F5] px-3 py-2.5" data-testid="group-invite-entities">
                            <div className="text-[10px] uppercase tracking-wider text-[#5E636E]">All entities in this invite ({groupTarget.clients.length})</div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                                {groupTarget.clients.map((c) => (
                                    <span
                                        key={c.id}
                                        className="inline-flex items-center rounded-md border border-[#E4E4E7] bg-white px-2 py-0.5 text-[11px] text-[#333]"
                                        data-testid={`group-invite-entity-${c.id}`}
                                    >
                                        {c.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {groupBusy && !groupPreview ? (
                        <div className="flex-1 min-h-[420px] grid place-items-center text-[#5E636E] text-sm">
                            <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading preview…</span>
                        </div>
                    ) : groupPreview ? (
                        <EditableEmailPreview
                            ref={groupEditorRef}
                            initialSubject={groupPreview.subject}
                            initialHtml={groupPreview.html}
                            toEmail={groupPreview.to || groupTarget?.email}
                        />
                    ) : null}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setGroupTarget(null); setGroupPreview(null); }}
                            disabled={groupBusy}
                            data-testid="group-invite-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmGroupInvite}
                            disabled={groupBusy || !groupPreview}
                            className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                            data-testid="group-invite-send-confirm"
                        >
                            {groupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send group invite</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Custom group invite builder — for entities with DIFFERENT emails */}
            <Dialog
                open={customOpen}
                onOpenChange={(o) => setCustomOpen(o)}
            >
                <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="custom-group-builder-dialog">
                    <DialogHeader>
                        <DialogTitle>Group invite — different emails</DialogTitle>
                        <DialogDescription>
                            Attach any set of entities to a single portal login, even when their <code>contact_email</code> fields don&apos;t match. The person only signs in with the address you type below.
                        </DialogDescription>
                    </DialogHeader>

                    {/* Target login */}
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                        <div>
                            <Label htmlFor="custom-email">Portal login email</Label>
                            <Input
                                id="custom-email"
                                type="email"
                                autoFocus
                                placeholder="sarah@company.co.nz"
                                value={customEmail}
                                onChange={(e) => setCustomEmail(e.target.value)}
                                data-testid="custom-group-email"
                            />
                            {customEmail && (() => {
                                const match = portalUsers.find((u) => (u.email || "").toLowerCase() === customEmail.trim().toLowerCase());
                                return (
                                    <div className="mt-1 text-[11px]" data-testid="custom-group-email-status">
                                        {match ? (
                                            <span className="text-emerald-700">
                                                <CheckCircle2 className="inline h-3 w-3 mr-1" />
                                                Existing portal user — entities will be added to their login (no email sent)
                                            </span>
                                        ) : (
                                            <span className="text-[#5E636E]">
                                                <Sparkles className="inline h-3 w-3 mr-1" />
                                                New portal user — an activation email will be drafted for your review
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                        <div>
                            <Label htmlFor="custom-name">Display name (optional)</Label>
                            <Input
                                id="custom-name"
                                placeholder="Sarah Jones"
                                value={customName}
                                onChange={(e) => setCustomName(e.target.value)}
                                data-testid="custom-group-name"
                            />
                        </div>
                    </div>

                    {/* Client picker */}
                    <div className="mt-2 flex flex-col min-h-0 flex-1">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                            <Label>Entities to link ({customPickedIds.length} picked)</Label>
                            {customPickedIds.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setCustomPickedIds([])}
                                    className="text-[11px] text-[#5E636E] hover:text-[#0F1115] underline"
                                    data-testid="custom-group-clear"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-[#5E636E]" />
                            <Input
                                placeholder="Search by client name or IRD number…"
                                value={customSearch}
                                onChange={(e) => setCustomSearch(e.target.value)}
                                className="pl-8"
                                data-testid="custom-group-search"
                            />
                        </div>
                        <div className="mt-2 rounded-md border border-[#E4E4E7] bg-white overflow-y-auto flex-1 min-h-[220px] max-h-[340px]" data-testid="custom-group-clients">
                            {customLoading ? (
                                <div className="p-6 text-center text-sm text-[#5E636E]">
                                    <Loader2 className="h-4 w-4 animate-spin inline" /> Loading clients…
                                </div>
                            ) : (() => {
                                const q = customSearch.trim().toLowerCase();
                                const rows = allClients
                                    .filter((c) => !q
                                        || (c.name || "").toLowerCase().includes(q)
                                        || (c.external_id || "").toLowerCase().includes(q)
                                        || (c.contact_email || "").toLowerCase().includes(q))
                                    .slice(0, 200);
                                if (rows.length === 0) {
                                    return <div className="p-4 text-xs text-[#5E636E]">No matching clients.</div>;
                                }
                                return rows.map((c) => {
                                    const picked = customPickedIds.includes(c.id);
                                    return (
                                        <label
                                            key={c.id}
                                            className={`flex items-center gap-2 px-3 py-2 border-b border-[#F1F1F3] last:border-b-0 cursor-pointer text-sm ${picked ? "bg-emerald-50/60" : "hover:bg-zinc-50"}`}
                                            data-testid={`custom-group-client-${c.id}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={picked}
                                                onChange={() => {
                                                    setCustomPickedIds((ids) => picked ? ids.filter((i) => i !== c.id) : [...ids, c.id]);
                                                }}
                                            />
                                            <span className="flex-1 min-w-0">
                                                <span className="font-medium truncate block">{c.name}</span>
                                                <span className="text-[11px] text-[#5E636E]">
                                                    {c.external_id ? `IRD ${c.external_id}` : "No IRD on file"}
                                                    {c.contact_email ? ` · ${c.contact_email}` : " · No email on file"}
                                                </span>
                                            </span>
                                        </label>
                                    );
                                });
                            })()}
                        </div>
                        <p className="mt-1.5 text-[11px] text-[#5E636E]">
                            The <code>contact_email</code> on each entity is irrelevant to login — the person signs in with the address at the top of this dialog.
                        </p>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setCustomOpen(false)}
                            data-testid="custom-group-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={launchCustomPreview}
                            disabled={!customEmail.trim() || customPickedIds.length === 0}
                            className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                            data-testid="custom-group-next"
                        >
                            <Send className="h-4 w-4" />
                            {(() => {
                                const match = portalUsers.find((u) => (u.email || "").toLowerCase() === customEmail.trim().toLowerCase());
                                return match
                                    ? `Link ${customPickedIds.length || 0} to existing login`
                                    : "Preview & send invite";
                            })()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div>
    );
}

function Tile({ n, l, tone, testId }) {
    const cls = tone === "blue" ? "text-blue-700"
        : tone === "green" ? "text-emerald-700"
        : tone === "amber" ? "text-amber-700"
        : "text-[#0F1115]";
    return (
        <div className="co-card co-card-pad" data-testid={testId}>
            <div className={`font-display text-3xl font-medium leading-none ${cls}`}>{n}</div>
            <div className="mt-2 text-xs text-[#5E636E]">{l}</div>
        </div>
    );
}

function ResultStat({ n, l, tone }) {
    const cls = tone === "green" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-[#0F1115]";
    return (
        <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3">
            <div className={`font-display text-2xl font-medium ${cls}`}>{n}</div>
            <div className="text-[11px] text-[#5E636E] mt-1">{l}</div>
        </div>
    );
}
